import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { useTable, useFilters } from "react-table";
import AnimatedTaps from "../components/AnimatedTaps";
import TankStatus from "../components/TankStatus";
import "../styles/Dashboard.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const DailyReport = ({ username }) => {
    const now = new Date();
    const [data, setData] = useState(null);
    const [tankData, setTankData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const currentYear = now.getFullYear();
    const currentMonthNumber = String(now.getMonth() + 1).padStart(2, "0");

    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonthNumber, setSelectedMonthNumber] = useState(currentMonthNumber);

    const yearOptions = useMemo(() => {
        return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }, [currentYear]);

    const monthOptions = useMemo(
        () => [
            { value: "01", label: "Januari" },
            { value: "02", label: "Februari" },
            { value: "03", label: "Maret" },
            { value: "04", label: "April" },
            { value: "05", label: "Mei" },
            { value: "06", label: "Juni" },
            { value: "07", label: "Juli" },
            { value: "08", label: "Agustus" },
            { value: "09", label: "September" },
            { value: "10", label: "Oktober" },
            { value: "11", label: "November" },
            { value: "12", label: "Desember" },
        ],
        []
    );

    const selectedMonth = `${selectedYear}-${selectedMonthNumber}`;

    const selectedPeriodLabel = useMemo(() => {
        const selectedMonthDate = new Date(`${selectedYear}-${selectedMonthNumber}-01T00:00:00`);
        return selectedMonthDate.toLocaleDateString("id-ID", {
            month: "long",
            year: "numeric",
        });
    }, [selectedYear, selectedMonthNumber]);

    const getToken = () => {
        const token = localStorage.getItem("token");
        if (!token) return null;
        return `Bearer ${token}`;
    };

    const fetchData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            window.location.href = "/login";
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await axios.get(
                `${API_BASE_URL}/reports/${username}/daily?month=${selectedMonth}`,
                {
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                    },
                }
            );
            setData(res.data);

            const tankRes = await axios.get(`${API_BASE_URL}/tanks/${username}`, {
                headers: { Authorization: token },
            });
            setTankData(tankRes.data);
        } catch (err) {
            console.error("❌ Error fetching daily report:", err);
            setError("Gagal mengambil data laporan harian.");
        } finally {
            setLoading(false);
        }
    }, [username, selectedMonth]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const groupedData = useMemo(() => {
        if (!data?.daily) return [];

        return data.daily
            .map((d) => {
                const rawDate = new Date(d.date);
                return {
                    rawDate: rawDate,
                    date: rawDate.toLocaleDateString("id-ID", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                    }),
                    volume: parseFloat(d.volume) || 0,
                    gallons: parseFloat(d.gallons) || 0,
                    tapA: d.tapA || 0,
                    tapB: d.tapB || 0,
                    tapC: d.tapC || 0,
                };
            })
            .sort((a, b) => a.rawDate - b.rawDate);
    }, [data]);

    const isDataEmpty = groupedData.length === 0;

    const summary = useMemo(() => {
        const totalVolume = groupedData.reduce((sum, item) => sum + item.volume, 0);
        const totalGallons = groupedData.reduce((sum, item) => sum + item.gallons, 0);
        const totalTapA = groupedData.reduce((sum, item) => sum + item.tapA, 0);
        const totalTapB = groupedData.reduce((sum, item) => sum + item.tapB, 0);
        const totalTapC = groupedData.reduce((sum, item) => sum + item.tapC, 0);

        return {
            totalVolume,
            totalGallons,
            totalDays: groupedData.length,
            totalTapA,
            totalTapB,
            totalTapC,
        };
    }, [groupedData]);

    const columns = useMemo(
        () => [
            { Header: "Tanggal", accessor: "date" },
            {
                Header: "Volume (L)",
                accessor: "volume",
                Cell: ({ value }) => Number(value).toFixed(2),
            },
            { Header: "Total Galon", accessor: "gallons" },
            { Header: "Keran A (Galon)", accessor: "tapA" },
            { Header: "Keran B (Galon)", accessor: "tapB" },
            { Header: "Keran C (Galon)", accessor: "tapC" },
        ],
        []
    );

    const tableInstance = useTable({ columns, data: groupedData }, useFilters);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    return (
        <div className="report-card-shell">
            <div className="report-topbar">
                <div>
                    <h3 className="report-title">Laporan Harian</h3>
                    <p className="report-subtitle">
                        Ringkasan penggunaan air untuk periode {selectedPeriodLabel}.
                    </p>
                </div>

                <button className="refresh-btn" onClick={fetchData}>
                    Refresh Data
                </button>
            </div>

            <div className="report-filters">
                <div className="form-group">
                    <label>Tahun</label>
                    <select
                        className="report-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Bulan</label>
                    <select
                        className="report-select"
                        value={selectedMonthNumber}
                        onChange={(e) => setSelectedMonthNumber(e.target.value)}
                    >
                        {monthOptions.map((month) => (
                            <option key={month.value} value={month.value}>
                                {month.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <p className="loading">Memuat data...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : isDataEmpty ? (
                <p className="report-empty">Belum ada data harian untuk {selectedPeriodLabel}.</p>
            ) : (
                <>
                    <div className="report-summary-grid">
                        <div className="report-summary-card">
                            <span className="summary-label">Total Volume</span>
                            <strong className="summary-value">
                                {summary.totalVolume.toFixed(2)} L
                            </strong>
                            <small className="summary-subvalue">{selectedPeriodLabel}</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Total Galon</span>
                            <strong className="summary-value">{summary.totalGallons}</strong>
                            <small className="summary-subvalue">Akumulasi seluruh hari</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Hari Terekam</span>
                            <strong className="summary-value">{summary.totalDays}</strong>
                            <small className="summary-subvalue">Jumlah hari dengan data</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Total Keran A / B / C</span>
                            <strong className="summary-value">
                                {summary.totalTapA} / {summary.totalTapB} / {summary.totalTapC}
                            </strong>
                            <small className="summary-subvalue">Total galon per keran</small>
                        </div>
                    </div>

                    <div className="chart-container">
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            height="360px"
                            data={[
                                ["Tanggal", "Volume (L)", "Galon"],
                                ...groupedData.map((d) => [d.date, Number(d.volume), Number(d.gallons)]),
                            ]}
                            options={{
                                title: `Trend Penggunaan Air Harian - ${selectedPeriodLabel}`,
                                curveType: "function",
                                legend: { position: "top" },
                                colors: ["#0ea5e9", "#22c55e"],
                                series: {
                                    0: { targetAxisIndex: 0 },
                                    1: { targetAxisIndex: 1 },
                                },
                                vAxes: {
                                    0: { title: "Volume (L)" },
                                    1: { title: "Galon" },
                                },
                                chartArea: {
                                    left: 75,
                                    top: 55,
                                    width: "88%",
                                    height: "72%",
                                },
                            }}
                        />
                    </div>

                    <div className="tap-and-tank-wrapper">
                        <div className="animated-taps">
                            <AnimatedTaps
                                tapA={data?.tapA || 0}
                                tapB={data?.tapB || 0}
                                tapC={data?.tapC || 0}
                            />
                        </div>

                        <TankStatus tankData={tankData} />
                    </div>

                    <div className="dashboard-table-wrapper">
                        <table {...getTableProps()} className="dashboard-table">
                            <thead>
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                        {headerGroup.headers.map((column) => (
                                            <th {...column.getHeaderProps()} key={column.id}>
                                                {column.render("Header")}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {rows.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()} key={row.id}>
                                            {row.cells.map((cell) => (
                                                <td {...cell.getCellProps()} key={cell.column.id}>
                                                    {cell.render("Cell")}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default DailyReport;